import { endOfDay, startOfDay, sub, toDate } from 'date-fns';
import {
  cloneDeep,
  extend,
  has,
  isArray,
  isEmpty,
  isNumber,
  isObject,
  map,
  uniq,
} from 'lodash';

import {
  DATE_FILTER_SELECTIONS,
  DAYS_AGO_OR_OLDER_TYPE,
  MAX_DAYS_AGO,
  NOT_FULL_DAY_FILTERS,
} from './constants';

// Guards saved/reconstructed models: a corrupt or non-numeric daysAgo would
// otherwise reach sub() and crash at toISOString() on an invalid date.
const isValidDaysAgo = (value) => {
  const days = Number(value);
  return Number.isInteger(days) && days >= 1 && days <= MAX_DAYS_AGO;
};

export function getMinValidDate() {
  return startOfDay(sub(new Date(), { months: 12 })); // Data available 365 days back
}

export function getInitialDateRange() {
  const today = new Date();
  const dateFrom = toDate(
    startOfDay(sub(new Date(), { months: 4 })),
  ).toISOString();
  const dateTo = today.toISOString();
  return {
    dateFrom,
    dateTo,
  };
}

function getCreatedAtDefaultFilterModel() {
  return {
    created_at: {
      ...getInitialDateRange(),
      filterType: 'date',
      type: 'inRange',
      filterParams: {
        minValidDate: getMinValidDate(),
      },
    },
  };
}

function replaceNulls(arr) {
  return arr.map((val) => (val === null ? '' : val));
}

const drayageFilterModel = {
  shipment_type: {
    condition1: {
      type: 'equals',
      filter: 'drayage',
      filterType: 'text',
    },
    condition2: {
      type: 'equals',
      filter: 'rail',
      filterType: 'text',
    },
    operator: 'OR',
  },
};

const shorthaulFilterModel = {
  shipment_type: {
    condition1: {
      type: 'equals',
      filter: 'shorthaul',
      filterType: 'text',
    },
    condition2: {
      type: 'equals',
      filter: 'longhaul',
      filterType: 'text',
    },
    condition3: {
      type: 'equals',
      filter: 'dayrate',
      filterType: 'text',
    },
    operator: 'OR',
  },
};

/*
 * multiLineFilter is used to create a query string for multi-line items
 * @parms{Object} filterModel is the actual filter
 * @params{String} field is the name of the field
 * @returns an object with conditions for each word to be queried
 * */
function makeMultiItemFilter(field, filterModel) {
  const conditionMap = filterModel.reduce((map, val, i) => {
    map[`condition${i + 1}`] = {
      filter: val[field],
      type: 'equals',
      filterType: 'text',
    };
    return map;
  }, {});

  conditionMap.operator = 'OR';
  return {
    [field]: conditionMap,
  };
}

// Used for the relative date selection list (TIME_SELECTIONS + DAYS_AGO_SELECTIONS)
const calculateDateFilterRange = (model) => {
  // Free-form "X+ Days Ago": records on or before end of (today - X days).
  if (model.type === DAYS_AGO_OR_OLDER_TYPE && isValidDaysAgo(model.daysAgo)) {
    return {
      dateFrom: getMinValidDate().toISOString(),
      dateTo: endOfDay(
        sub(new Date(), { days: Number(model.daysAgo) }),
      ).toISOString(),
      filterType: 'date',
      type: 'inRange',
    };
  }

  const rangeParams = DATE_FILTER_SELECTIONS.find(
    (item) => item.localizationSlug === model.type || item.name === model.type,
  );

  if (rangeParams) {
    return {
      dateFrom: rangeParams.start().toISOString(),
      dateTo: rangeParams.end().toISOString(),
      filterType: 'date',
      // setting custom type to prevent overrides, standard will be returned in APIUtils before actual API call
      type: NOT_FULL_DAY_FILTERS.includes(model.type)
        ? 'timeSelectionInRange'
        : 'inRange',
    };
  }

  return model;
};
export function getModelParams({ view, filterModel, agGridApi }) {
  let modelParams =
    view === 'drayage'
      ? extend({}, drayageFilterModel, filterModel)
      : extend({}, shorthaulFilterModel, filterModel);

  if (isEmpty(modelParams.created_at)) {
    const createdAtDefaultFilterModel = getCreatedAtDefaultFilterModel();
    const createdAtFilterInstance = agGridApi.getFilterInstance('created_at');
    if (!isEmpty(createdAtFilterInstance)) {
      createdAtFilterInstance.setModel(createdAtDefaultFilterModel?.created_at);
    }
    modelParams = extend({}, createdAtDefaultFilterModel, modelParams);
  }

  const fieldValues = Object.keys(filterModel) || [];

  fieldValues.forEach((field) => {
    let filterValues = [];
    let textFilters = [];

    // Change model of TIME_SELECTIONS date filters to inRange based on calculated dates
    if (modelParams[field].filterType === 'date') {
      if (has(modelParams[field], 'conditions')) {
        // Why do we need condition1 and condition2 if we have an array of conditions?
        modelParams[field].condition1 = calculateDateFilterRange(
          modelParams[field].condition1,
        );
        modelParams[field].condition2 = calculateDateFilterRange(
          modelParams[field].condition2,
        );
        modelParams[field].conditions = modelParams[field].conditions.map(
          (condition) => calculateDateFilterRange(condition),
        );
      } else {
        modelParams[field] = calculateDateFilterRange(modelParams[field]);
      }
    }

    if (has(filterModel[field], 'filterModels')) {
      filterModel[field].filterModels.forEach((val) => {
        if (has(val, 'value')) {
          filterValues.push(val.value);
        } else if (has(val, 'filter')) {
          textFilters.push(val);
        }
        if (val) {
          const conditions = Object.keys(val).filter((key) =>
            key.startsWith('condition'),
          );
          if (conditions.length) {
            conditions.forEach((conditionKey) => {
              textFilters.push(val[conditionKey]);
            });
          }
        }
      });
    }
    if (has(filterModel[field], 'value')) {
      const value = filterModel[field].value;
      filterValues.push(value);
    }
    if (filterValues.length > 0) {
      filterValues.forEach((value) => {
        if (isObject(value) && !isArray(value)) {
          if (value.includeText) {
            modelParams[field] = {
              type: 'startsWith',
              filter: value.includeText,
            };
          } else if (value.excludeText) {
            modelParams[field] = {
              type: 'notStartsWith',
              filter: value.excludeText,
            };
          } else {
            delete modelParams[field];
          }
        } else {
          const multiItemFilter = value || [];
          if (multiItemFilter.length > 0) {
            const equipParam = makeMultiItemFilter(field, multiItemFilter);
            delete modelParams[field];
            modelParams = extend({}, equipParam, modelParams);
          } else {
            delete modelParams[field];
          }
        }
      });
    }
    if (textFilters.length > 0) {
      if (has(modelParams[field], 'filterModels')) {
        delete modelParams[field].filterModels;
      }

      const existing = modelParams[field];
      Object.keys(existing).forEach((prop) => {
        if (prop.includes('condition')) {
          textFilters.push(existing[prop]);
        }
      });
      const conditionMap = textFilters.reduce((map, val, i) => {
        map[`condition${i + 1}`] = { ...val };
        return map;
      }, {});

      // overriding operator in any case so make sure text filter has other options supressed
      conditionMap.operator = 'OR';

      const equipParam = {
        [field]: conditionMap,
      };
      delete modelParams[field];
      modelParams = extend({}, equipParam, modelParams);
    }
  });

  return modelParams;
}

export function transformDateCondition(incoming = {}) {
  const model = cloneDeep(incoming);
  const conditionsThatUseStartOfDayOnly = [
    'equals',
    'notEqual',
    'before',
    'after',
  ];
  if (model.filterType !== 'date') {
    return model;
  }
  if (
    incoming.type === DAYS_AGO_OR_OLDER_TYPE &&
    isValidDaysAgo(incoming.daysAgo)
  ) {
    model.dateFrom = getMinValidDate();
    model.dateTo = endOfDay(
      sub(new Date(), { days: Number(incoming.daysAgo) }),
    );
    return model;
  }
  if (incoming.type) {
    const timeSel = DATE_FILTER_SELECTIONS.find(
      (timeSel) => timeSel.localizationSlug === incoming.type,
    );
    if (timeSel) {
      model.dateFrom = timeSel.start();
      model.dateTo = timeSel.end();
      return model;
    }
  }
  if (model.type !== 'timeSelectionInRange') {
    model.dateFrom = model.dateFrom
      ? toDate(startOfDay(model.dateFrom)).toISOString()
      : null;
    if (conditionsThatUseStartOfDayOnly.includes(model.type)) {
      model.dateTo = model.dateFrom
        ? toDate(endOfDay(model.dateFrom))?.toISOString()
        : null;
    } else {
      model.dateTo = model.dateTo
        ? toDate(endOfDay(model.dateTo))?.toISOString()
        : null;
    }
  } else {
    if (model.dateFrom) {
      model.dateFrom = new Date(model.dateFrom)?.toISOString();
    }
    if (model.dateTo) {
      model.dateTo = new Date(model.dateTo)?.toISOString();
    }
  }
  return model;
}

export function transformFilterModelQuery(incoming = {}) {
  const dateModel = cloneDeep(incoming);
  const newModel = {};

  map(dateModel, (model, key) => {
    // two or more conditions are changed into "date" : {"condition1" : {"filterType": "date"}, "condition2" : {"filterType": "date"}}
    if (model.conditions || model.condition1 || model.condition2) {
      Object.keys(model).forEach((objKey) => {
        if (objKey === 'conditions') {
          model.conditions = model.conditions.map((condition) => {
            return transformDateCondition(condition);
          });
        } else if (objKey.startsWith('condition')) {
          model[objKey] = transformDateCondition(model[objKey]);
        }
      });
    }
    // in case of only one condition "date": {"filterType": "date"}
    else if (model.filterType === 'date') {
      model = transformDateCondition(model);
    }

    if (!isEmpty(model)) {
      newModel[key] = model;
    }
  });

  if (newModel.shipper_name) {
    let equalsConditions = {};
    let nonEqualsConditions = {};

    Object.entries(newModel.shipper_name).forEach(([key, condition]) => {
      if (condition.type === 'equals') {
        equalsConditions[key] = condition;
      } else {
        nonEqualsConditions[key] = condition;
      }
    });

    newModel.shipper_oid = {
      ...equalsConditions,
      ...newModel.shipper_oid,
      operator: newModel.shipper_name.operator,
    };
    newModel.shipper_name = {
      ...nonEqualsConditions,
      operator: newModel.shipper_name.operator,
    };
  }

  if (newModel.carrier_subsidiary?.values?.includes(null)) {
    const carrierSubsidiaryValues = newModel?.carrier_subsidiary?.values;
    newModel.carrier_subsidiary.values = replaceNulls(carrierSubsidiaryValues);
  }

  if (newModel.shipper_subsidiary?.values?.includes(null)) {
    const shipperSubsidiaryValues = newModel?.shipper_subsidiary?.values;
    newModel.shipper_subsidiary.values = replaceNulls(shipperSubsidiaryValues);
  }

  if (newModel.margin_pct) {
    const divideFilterBy100 = (condition) => {
      if (condition && isNumber(condition.filter)) {
        condition.filter = condition.filter / 100;
      }
      if (condition && isNumber(condition.filterTo)) {
        condition.filterTo = condition.filterTo / 100;
      }
      return condition;
    };

    if (newModel.margin_pct.condition1 || newModel.margin_pct.condition2) {
      Object.keys(newModel.margin_pct).forEach((key) => {
        if (['condition1', 'condition2'].includes(key)) {
          newModel.margin_pct[key] = divideFilterBy100(
            newModel.margin_pct[key],
          );
        }
      });
      newModel.margin_pct.conditions = [
        newModel.margin_pct.condition1,
        newModel.margin_pct.condition2,
      ];
    } else {
      newModel.margin_pct = divideFilterBy100(newModel.margin_pct);
    }
  }

  return newModel;
}

export function loadColumnState(incomingFilter, api, blacklistedColumns = []) {
  const filter = { ...incomingFilter };

  filter.hidden = uniq([...(filter.hidden ?? []), ...blacklistedColumns]);
  const columnState = api.getColumnState();
  const visibleColumns =
    filter.columnOrder?.length > 0
      ? filter.columnOrder
          .reduce((orderedColumns, colId) => {
            const col = columnState.find((col) => col.colId === colId);
            if (col) {
              return orderedColumns.concat(col);
            }
            return orderedColumns;
          }, [])
          .map((col) => {
            const hide = filter.hidden.includes(col.colId);
            return {
              ...col,
              hide,
            };
          })
      : columnState;

  const hiddenColumns = columnState
    .filter((col) => {
      return (
        filter.hidden.includes(col.field) &&
        !filter.columnOrder.includes(col.field)
      );
    })
    .map((col) => ({ ...col, hide: true }));

  const otherColumns = columnState
    .filter((col) => {
      return !filter.columnOrder.includes(col.colId);
    })
    .map((col) => ({ ...col, hide: true }));

  const updatedColumnState = visibleColumns
    .concat(...hiddenColumns, ...otherColumns)
    .map((col) => {
      const pinnedColumn = filter.pinned.find(
        (pinnedCol) => pinnedCol.field === col.colId,
      );
      const pinned = pinnedColumn ? pinnedColumn.pinned : false;
      return { ...col, pinned };
    });

  const updatedColumnStateWithWidth = updatedColumnState.map((item) => ({
    ...item,
    colId: item.colId,
    width:
      filter?.rowColumnsSize?.find((x) => x.field === item.colId)?.size ||
      item.width,
  }));

  const updatedColumnStateWithWidthAndSorting = updatedColumnStateWithWidth.map(
    (item) => ({
      ...item,
      colId: item.colId,
      sort:
        filter?.rowColumnsSorting?.find((x) => x.field === item.colId)?.sort ||
        item.sort,
      sortIndex: isNumber(
        filter?.rowColumnsSorting?.find((x) => x.field === item.colId)
          ?.sortIndex,
      )
        ? filter?.rowColumnsSorting?.find((x) => x.field === item.colId)
            ?.sortIndex
        : null,
    }),
  );

  api.applyColumnState({
    state: updatedColumnStateWithWidthAndSorting,
    applyOrder: true,
  });
}
